import { isMiniAppShell } from "@/shared/lib/appShell";
/**
 * ФАКТИЧЕСКИЕ пути роутеров приложения — источник валидации `meta.route`
 * (бэк присылает готовый маршрут, фронт уважает его только если такой путь
 * реально объявлен, см. resolveNotificationRoute).
 *
 * Списки сняты с самих роутеров, а не с массива `routes` в App.tsx (тот нужен
 * лишь для matchRoutes-проверки «показывать ли шелл» и содержит пути, которых
 * в текущем шелле может не быть):
 *   - веб  — `widgets/web/content/ui/Content.tsx`
 *   - миниап — `widgets/miniapp/miniapp-content/ui/MiniAppContent.tsx`
 *
 * Наборы РАЗНЫЕ: миниап — подмножество веба (нет /create, /my-photos, /account,
 * /support и прочих «фото/видео»-разделов). Поэтому валидируем по активному
 * шеллу: иначе клик по уведомлению с веб-only маршрутом открывал бы в миниаппе
 * пустой экран (Routes не находит совпадения).
 */
/** Роутер веб-шелла (авторизованная ветка Content.tsx — она самая широкая). */
const WEB_ROUTES = new Set([
    "/",
    "/ui",
    "/create",
    "/my-videos",
    "/my-poems",
    "/my-songs",
    "/extend-song",
    "/edit-track",
    "/video-cover",
    "/karaoke",
    "/release",
    "/my-releases",
    "/my-photos",
    "/my-greetings",
    "/my-vocal-removal",
    "/my-portraits",
    "/my-clips",
    "/generate-greetings",
    "/generate-vocal-removal",
    "/generate-portrait",
    "/generate-clip",
    "/generate-video",
    "/generate-poem",
    "/generate-song",
    "/merge-photos",
    "/animate-photo",
    "/balance",
    "/referral",
    "/account",
    "/payment/success",
    "/support",
    "/faq",
    "/charts",
    "/share",
]);
/** Роутер миниапа (авторизованная ветка MiniAppContent.tsx). */
const MINIAPP_ROUTES = new Set([
    "/",
    "/ui",
    "/my-songs",
    "/extend-song",
    "/edit-track",
    "/video-cover",
    "/karaoke",
    "/release",
    "/my-releases",
    "/generate-song",
    "/balance",
    "/referral",
    "/payment/success",
    "/faq",
    "/charts",
    "/share",
]);
/**
 * Параметрические пути, доступные в ЛЮБОМ шелле: `/song/:token` и `/s/:token`
 * объявлены на уровне App.tsx (публичные full-screen страницы вне Content).
 * Валидируем по префиксу + непустому хвосту.
 */
const SHARED_DYNAMIC_PREFIXES = ["/song/", "/s/"];
/**
 * Параметрические пути ТОЛЬКО веб-шелла. `/user/:id` объявлен исключительно в
 * `Content.tsx` — в `MiniAppContent.tsx` такого роута нет, поэтому в миниапе
 * ссылка на него открыла бы пустой экран.
 */
const WEB_DYNAMIC_PREFIXES = ["/user/"];
/** Путь без query/hash и без хвостового слэша (кроме корня). */
export const routePathOf = (route) => {
    const path = route.split("#")[0].split("?")[0].trim();
    if (path.length > 1 && path.endsWith("/"))
        return path.slice(0, -1);
    return path;
};
/**
 * Объявлен ли такой путь в роутере активного шелла. Абсолютные URL и любые
 * неизвестные пути отвергаем — вызывающий откатится на собственный маппинг.
 */
export const isKnownAppRoute = (route) => {
    const path = routePathOf(route);
    if (!path.startsWith("/"))
        return false;
    const miniApp = isMiniAppShell();
    const prefixes = miniApp
        ? SHARED_DYNAMIC_PREFIXES
        : [...SHARED_DYNAMIC_PREFIXES, ...WEB_DYNAMIC_PREFIXES];
    if (prefixes.some((prefix) => path.startsWith(prefix) && path.length > prefix.length)) {
        return true;
    }
    return (miniApp ? MINIAPP_ROUTES : WEB_ROUTES).has(path);
};
